import { useCallback } from "react";
import {
  GuaranteeType,
  PackagedBeerDetailsContainer,
  ProposalStates,
  useGetPointOfSalesLazyQuery,
  useGetProposalInformationLazyQuery,
} from "../../../../gql/hooks";
import useAppDispatch from "../../../hooks/useAppDispatch";
import {
  AddMaterialsToProposal,
  AddProductsToProposal,
  initialState,
  ResetProposalState,
  SetProposalInfo,
  SetProposalLoading,
} from "..";
import { useIonRouter } from "@ionic/react";
import { errorLog } from "../../../../utils/debugLog";
import useAppSelector from "../../../hooks/useAppSelector";
import {
  ProposalReduxState,
  ProposalTypeOfVisit,
  ResumeFieldsType,
} from "../types";
import getCorrectProducts from "../utils/getCorrectProducts";
import getContainerType from "../utils/getContainerType";
import {
  getCommercialConditionMaterials,
  getCommercialConditionProducts,
} from "../utils/getCommercialConditionProduct";
import { AddProductsToProposalType } from "../reducers/productsReducer";
import { AddMaterialToProposalType } from "../reducers/materialsReducer";
import getDraftSynthesis from "../../../../pages/ProposalCreation/hooks/library/getDraftSynthesis";
import { defaultResumenFields } from "../constants";
import { createProposalPanePaths } from "../../../../pages/ProposalCreation/hooks/useProposalNavigation";

const getValue = (value: any, defaultValue: any = undefined) =>
  value || defaultValue;

const useOldProposal = () => {
  const dispatch = useAppDispatch();
  const { push } = useIonRouter();

  const [getPosInfo] = useGetPointOfSalesLazyQuery();
  const [getProposalInformation] = useGetProposalInformationLazyQuery({
    fetchPolicy: "network-only",
  });

  const isLoading = useAppSelector((state) => state.proposal.isLoading);

  const onOldProposalUse = useCallback(
    async (proposalId: number) => {
      if (!proposalId) return;

      dispatch(ResetProposalState());
      dispatch(SetProposalLoading(true));

      try {
        const { data } = await getProposalInformation({
          variables: { proposalId },
        });
        const draftProposal = data?.proposal;

        if (draftProposal) {
          const isTemplate = draftProposal.state === ProposalStates.Template;

          // Get Pos info in cas of POS
          let posInfo = undefined;

          if (!isTemplate && draftProposal.pointOfSaleId) {
            const { data: posData } = await getPosInfo({
              variables: { id: draftProposal.pointOfSaleId },
            });
            const retrievedData = posData?.pointOfSales.find(
              ({ id }) => id === draftProposal.pointOfSaleId,
            );
            posInfo = retrievedData;
          }

          // Get Proposal Resume and Guarantees
          let resumeFields: ResumeFieldsType = defaultResumenFields;
          let guarantees = undefined;

          if (draftProposal.synthesis) {
            resumeFields = getDraftSynthesis(draftProposal.synthesis);
            guarantees = {
              firstGuarantee: {
                type:
                  draftProposal.synthesis.firstGuaranteeType ||
                  GuaranteeType.None,
                amount: draftProposal.synthesis.firstGuaranteePercent || 0,
              },
              secondGuarantee: {
                type:
                  draftProposal.synthesis.secondGuaranteeType ||
                  GuaranteeType.None,
                amount: draftProposal.synthesis.secondGuaranteePercent || 0,
              },
              adsFirstGuarantee: {
                type:
                  draftProposal.synthesis.adsFirstGuaranteeType ||
                  GuaranteeType.None,
                amount: draftProposal.synthesis.adsFirstGuaranteePercent || 0,
              },
              adsSecondGuarantee: {
                type:
                  draftProposal.synthesis.adsSecondGuaranteeType ||
                  GuaranteeType.None,
                amount: draftProposal.synthesis.adsSecondGuaranteePercent || 0,
              },
            };
          }

          // Proposal Information Feeding time
          let proposal: ProposalReduxState = {
            ...initialState,

            isOld: true,
            proposalId: isTemplate ? undefined : proposalId,

            proposalInformation: {
              name: draftProposal.title,
              expireDay: Math.round(
                draftProposal.expireAt
                  ? Number(new Date(draftProposal.expireAt))
                  : Date.now() / 1000,
              ),
              posId: isTemplate
                ? undefined
                : getValue(draftProposal.pointOfSaleId),
              provinceNumber: isTemplate
                ? undefined
                : getValue(draftProposal.provinceNumber),
              pos: isTemplate ? undefined : posInfo,
              brandId: getValue(draftProposal.themeId),
              typeOfVisit: draftProposal.sherpaExternalId
                ? ProposalTypeOfVisit.BusinessDevelopment
                : ProposalTypeOfVisit.Default,
              sherpaExternalId: draftProposal.sherpaExternalId ?? undefined,
            },

            guarantees,
            resumeFields,

            isUpdated: false,
          };

          // Gather Products Data
          const { kegs, bottles, tanks, additionalProducts } = {
            kegs: getCorrectProducts(
              draftProposal.proposalAssortments,
              draftProposal.commercialConditions,
              PackagedBeerDetailsContainer.Keg,
            ),
            bottles: getCorrectProducts(
              draftProposal.proposalAssortments,
              draftProposal.commercialConditions,
              PackagedBeerDetailsContainer.Bottle,
            ),
            tanks: getCorrectProducts(
              draftProposal.proposalAssortments,
              draftProposal.commercialConditions,
              PackagedBeerDetailsContainer.Tank,
            ),
            additionalProducts: getContainerType(
              draftProposal.proposalAssortments,
            ),
          };

          const products = [
            ...getCommercialConditionProducts(
              kegs,
              draftProposal.commercialConditions,
              PackagedBeerDetailsContainer.Keg,
            ),
            ...getCommercialConditionProducts(
              tanks,
              draftProposal.commercialConditions,
              PackagedBeerDetailsContainer.Tank,
            ),
            ...getCommercialConditionProducts(
              bottles,
              draftProposal.commercialConditions,
              PackagedBeerDetailsContainer.Bottle,
            ),
          ] as AddProductsToProposalType[];

          const materials = getCommercialConditionMaterials(
            additionalProducts,
            draftProposal.commercialConditions,
            draftProposal.customCommercialCondition,
          ) as AddMaterialToProposalType[];

          // Put current Step
          let step = 0;
          if (!isTemplate && proposal.proposalInformation.expireDay) {
            ++step;
            if (proposal.proposalInformation.brandId) {
              ++step;
            }
          }
          proposal.maxStep = step;
          proposal.currentStep = step;

          // Add everything
          dispatch(SetProposalInfo(proposal));

          dispatch(AddProductsToProposal({ products }));
          dispatch(AddMaterialsToProposal({ materials }));

          // Route to proposal
          push(createProposalPanePaths[step]);
        }
        dispatch(SetProposalLoading(false));
      } catch (err) {
        errorLog(err, "Error on Old Proposal");
        dispatch(SetProposalLoading(false));
      }
    },
    [dispatch, push, getPosInfo, getProposalInformation],
  );

  return { onOldProposalUse, isLoading };
};

export default useOldProposal;
